import {
    mat4,
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    vec2,
    vec3,
} from 'gl-matrix';
import { gl } from '~/engine/Engine';
import { Renderer } from '~/engine/core/render/Renderer';
import { ShaderProgram } from '~/engine/core/utils/ShaderProgram';
import { UniformVec2 } from '~/engine/core/utils/uniforms/UniformVec2';
import { UniformFloat } from '~/engine/core/utils/uniforms/UniformFloat';
import { UniformVec3 } from '~/engine/core/utils/uniforms/UniformVec3';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { contourEdgeShaderChunk, crossSectionVertexShader } from '../glsl';
import { ContourEdgeUniforms } from '../util/contourEdgeUniforms';
import {
    AdjacentSliceGhostDepths,
    GHOST_DEPTH_ALPHA,
    GHOST_PATTERNS,
    ghostSideTowards,
    neighbouringSliceOffsetMm,
} from './adjacentSliceGhost';

/**
 * The plane a ghost draw is made on. Satisfied by {@link PlaneView}; kept
 * structural so the renderer can be driven against a bare framebuffer.
 */
export interface SlicePlaneGeometry {
    /** Patient space → view NDC; the displayed plane is NDC z = 0. */
    readonly projectionViewMatrix: ReadonlyMat4;
    /** The view's size in pixels. */
    readonly size: ReadonlyVec2;
    /** The view normal in patient space (unit length). */
    readonly viewDirection: ReadonlyVec3;
    /** The view's centre in patient space: a point on the displayed plane. */
    readonly position: ReadonlyVec3;
}

/**
 * Draws the adjacent-slice contour ghosts (MIM parity §5.9.1) of one ROI's
 * SDF: on the displayed slice, its contour from the chosen slices on each
 * side, as interrupted lines in its colour — in that side's pattern and
 * fainter the farther the slice — so a ghost is never mistaken for a contour
 * on the displayed slice, and sides and distances are told apart.
 *
 * Each ghost is the SDF's cross-section on the plane the wheel ticks would
 * display ({@link neighbouringSliceOffsetMm}), drawn where it projects onto
 * the displayed plane with the {@link ContourRenderer}'s edge detection, so
 * it shows wherever the displayed plane lies; stepping along the view normal
 * makes the neighbours on coronal and sagittal views the adjacent slices of
 * those views. Nothing is drawn beyond the image or the SDF's own box, nor
 * where that slice has no contour; every contour region on it is drawn. It
 * reads the SDF and never writes it.
 */
export class AdjacentSliceGhostRenderer implements Renderer {
    private readonly shader = new AdjacentSliceGhostShader();

    /**
     * Draw the ghosts of `texture` for `depths` on `view`, farthest first
     * across both sides so the nearer one shows where two coincide. Written
     * unblended: the target holds each ghost's straight colour and opacity,
     * for a compositor to apply once. `texture` must be resolved first: lazy
     * SDF generation would switch the GL program mid-draw.
     *
     * @param color The ROI colour, normalised RGB.
     * @param contourThickness Line thickness in pixels.
     */
    public render(
        view: SlicePlaneGeometry,
        texture: ContourTexture,
        color: ReadonlyVec3,
        contourThickness: number,
        depths: AdjacentSliceGhostDepths
    ): void {
        // One pass per side and depth, resolved before any GL state changes.
        const passes = ([1, -1] as const)
            .flatMap((sign) => {
                const direction = vec3.scale(
                    vec3.create(),
                    view.viewDirection,
                    sign
                );
                const side = ghostSideTowards(direction);
                return depths[side].map((depth) => ({
                    offsetMm: neighbouringSliceOffsetMm(
                        texture.grid,
                        view.position,
                        direction,
                        depth
                    ),
                    pattern: GHOST_PATTERNS[side],
                    depth,
                }));
            })
            .toSorted((a, b) => b.depth - a.depth);
        if (passes.length === 0) {
            return;
        }

        try {
            gl.disable(gl.BLEND);
            this.shader.start();

            this.shader.edge.load(
                view.projectionViewMatrix,
                view.size,
                texture,
                contourThickness
            );
            this.shader.color.loadVec3(color);

            texture.bindTexture();
            for (const { offsetMm, pattern, depth } of passes) {
                // That slice's plane cut against the SDF box, each point
                // placed on the displayed plane straight across from it.
                const vao = texture.crossSection(
                    mat4.translate(
                        mat4.create(),
                        view.projectionViewMatrix,
                        vec3.negate(vec3.create(), offsetMm)
                    )
                );
                if (!vao) {
                    continue;
                }
                try {
                    vao.bind(0, 1);
                    this.shader.dashPattern.loadVec2(
                        vec2.fromValues(pattern.onPx, pattern.periodPx)
                    );
                    this.shader.alpha.loadFloat(GHOST_DEPTH_ALPHA[depth]);
                    gl.drawElements(
                        gl.TRIANGLES,
                        vao.indexCount,
                        gl.UNSIGNED_INT,
                        0
                    );
                } finally {
                    vao.unbind(0, 1);
                    vao.delete();
                }
            }
        } finally {
            texture.unbindTexture();
            this.shader.stop();
        }
    }

    public delete(): void {
        this.shader.delete();
    }
}

/**
 * Draws, per fragment of a ghost slice's cross-section, the contour edge of
 * the SDF at that point, masked to a screen-space dash pattern whose
 * phase runs along the local contour (screen x or y, by the in-plane SDF
 * gradient), so dashes end cleanly and keep their size at every zoom.
 */
class AdjacentSliceGhostShader extends ShaderProgram {
    private static readonly FRAGMENT_CONTENT = `#version 300 es

    precision highp float;
    precision highp sampler3D;

    in vec3 pass_textureCoordinates;
    out vec4 outColor;

    uniform vec3 color;
    // Screen-space dash pattern as (pixels drawn, period), in pixels.
    uniform vec2 dashPattern;
    uniform float ghostAlpha;

    ${contourEdgeShaderChunk}

    void main() {
        vec3 center = pass_textureCoordinates;

        // The neighbouring slice must exist: inside the image stack on
        // every axis, and inside the SDF's own box, beyond which the
        // structure has no contour and sampling would clamp onto the
        // box's outermost texels.
        if (!isInsideImage(center) ||
            any(lessThan(center, vec3(0.0))) ||
            any(greaterThan(center, vec3(1.0)))) {
            discard;
        }

        bool isInside = texture(sampler, center).r >= SDF_THRESHOLD;
        if (!isContourEdge(center, isInside)) {
            discard;
        }

        // In-plane SDF gradient, one line thickness either side.
        vec3 dxUV = (viewToTextureUV * vec4(pixelSize.x, 0.0, 0.0, 0.0)).xyz;
        vec3 dyUV = (viewToTextureUV * vec4(0.0, pixelSize.y, 0.0, 0.0)).xyz;
        float gradX = texture(sampler, center + dxUV).r -
                      texture(sampler, center - dxUV).r;
        float gradY = texture(sampler, center + dyUV).r -
                      texture(sampler, center - dyUV).r;
        // A normal mostly along x means the contour, and the phase, run along y.
        float phase = abs(gradX) > abs(gradY) ? gl_FragCoord.y : gl_FragCoord.x;

        if (mod(phase, dashPattern.y) >= dashPattern.x) {
            discard;
        }

        outColor = vec4(color, ghostAlpha);
    }`;

    public readonly color = new UniformVec3('color');
    public readonly dashPattern = new UniformVec2('dashPattern');
    public readonly alpha = new UniformFloat('ghostAlpha');
    public readonly edge = new ContourEdgeUniforms();

    constructor() {
        super(
            crossSectionVertexShader,
            AdjacentSliceGhostShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.storeUniformLocations(
            this.color,
            this.dashPattern,
            this.alpha,
            ...this.edge.all
        );
    }
}
